<script lang="ts">
	/**
	 * In-room header: back + tab rail + kebab on one row; room name below.
	 * Finish reordering is owned here (not AddEditButtonsNew) so exit cannot
	 * die from remount / initialEdit desync / stale exit promises.
	 */
	import { format } from 'svelte-i18n';
	import { location, push } from 'svelte-spa-router';
	import { listHeader } from 'src/stores/list_header';
	import { edit, bulkActionEdit } from 'src/stores/ui/edit_lists';
	import {
		compactEditChrome,
		forceExitEditMode,
	} from 'src/helpers/edit_chrome_helpers';
	import type { HeaderTabsStyle } from 'src/stores/features/dev/header-chrome';
	import ListHeaderActions from 'src/components/Lists/ListHeaderActions.svelte';
	import { Tabs, Tab, IconButton, Breadcrumbs, ButtonV2 } from '@Shelly/shelly-design-system';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { deriveRoomName, getRoomTabKeys, pushRoomListType } from 'src/helpers/service_rooms_helpers';

	let { useNewLayout = true, tabsStyle }: { useNewLayout?: boolean; tabsStyle: HeaderTabsStyle } =
		$props();

	const roomId = $derived($location.split('/')[3] ?? '');
	const roomListType = $derived($location.split('/')[4] ?? '');
	const tabs = $derived(getRoomTabKeys(roomId));
	const roomName = $derived(deriveRoomName(roomId));
	const isEditChrome = $derived($compactEditChrome);
	/** Pure reorder (not bulk) — header owns the exit control. */
	const showFinishReorder = $derived($edit && !$bulkActionEdit);
</script>

<div
	class="room-header-chrome d-flex flex-direction-column mb-lg-m"
	class:is-edit-chrome={isEditChrome}
	class:is-finish-reorder={showFinishReorder}
>
	<div class="room-header-tabs-row">
		<div
			class="room-header-back d-flex align-items-center"
			class:is-concealed={isEditChrome}
			aria-hidden={isEditChrome}
		>
			<IconButton
				variant="muted"
				icon="fas fa-chevron-left"
				aria-label={$format('aria_back_room', { default: 'Back to room list' })}
				disabled={isEditChrome}
				on:click={() => {
					const roomBackRoute = '/home/rooms/-1';
					setSidePanelState({
						type: roomBackRoute,
						id: '-1',
					});
					push(roomBackRoute);
				}}
			/>
		</div>
		<div class="room-header-tabs">
			<Tabs
				{useNewLayout}
				{tabsStyle}
				location={tabs.findIndex((tab) => tab === roomListType)}
				hideScrollArrows
				hideSlotAction
				hideTabStrip={isEditChrome}
			>
				{#each tabs as tab (tab)}
					<Tab
						class="text-capitalize"
						{useNewLayout}
						{tabsStyle}
						active={tab === roomListType}
						hideRightSlot
						on:click={(event) => {
							event.preventDefault();
							pushRoomListType(roomId, tab);
						}}
					>
						{$format(`tab_${tab}`, { default: tab })}
					</Tab>
				{/each}
			</Tabs>
		</div>
	</div>

	{#if roomName}
		<div class="room-header-name">
			<Breadcrumbs
				aria-label={$format('wizard_step_room', { default: 'Room' })}
				links={[{ label: roomName }]}
				listClasses="width-full"
			/>
		</div>
	{/if}

	{#if showFinishReorder}
		<div class="room-header-finish">
			<ButtonV2
				size="m"
				variant="primary"
				width={isEditChrome ? 'full' : 'auto'}
				icon="fas fa-xmark"
				on:click={() => {
					// Guaranteed clear — do not depend on dissolve/inflight state machines
					// that previously left the control dead on a second reorder.
					forceExitEditMode();
				}}
			>
				{$format('label_finish_reordering', { default: 'Finish reordering' })}
			</ButtonV2>
		</div>
	{:else if $listHeader}
		<div class="room-header-actions">
			<ListHeaderActions />
		</div>
	{/if}
</div>

<style>
	.room-header-chrome {
		position: relative;
	}

	.room-header-tabs-row {
		position: relative;
		width: 100%;
	}

	.room-header-back {
		position: absolute;
		left: 0;
		top: 0;
		z-index: 4;
		height: 44px;
		opacity: 1;
		transform: translateX(0);
		transition:
			opacity 180ms ease,
			transform 220ms ease;
	}

	.room-header-back.is-concealed {
		opacity: 0;
		transform: translateX(-8px);
		pointer-events: none;
	}

	/* Tabs draws ::after on .tabs-wrapper; padding keeps labels clear of back + kebab. */
	.room-header-chrome:not(.is-edit-chrome) .room-header-tabs :global(.tabs-wrapper) {
		padding-left: calc(32px + var(--spacing-s));
		padding-right: calc(32px + var(--spacing-s));
	}

	.room-header-name {
		margin-top: var(--spacing-m);
		margin-right: 9px;
		transition: margin-top 220ms ease;
	}

	/* Idle kebab + desktop Finish: float over the tab rail. */
	.room-header-actions,
	.room-header-chrome:not(.is-edit-chrome) .room-header-finish {
		position: absolute;
		top: 0;
		right: 0;
		z-index: 5;
		height: 44px;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		opacity: 1;
		transform: translateY(0);
		transition:
			opacity 200ms ease,
			transform 200ms ease;
	}

	/* Peek → room name → Finish (mobile/tablet). */
	.room-header-chrome.is-edit-chrome {
		gap: var(--spacing-s);
	}

	.room-header-chrome.is-edit-chrome .room-header-name {
		margin-top: 0;
		margin-right: 0;
	}

	.room-header-chrome.is-edit-chrome .room-header-finish {
		position: relative;
		top: auto;
		right: auto;
		z-index: 5;
		height: auto;
		min-height: 44px;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: stretch;
		opacity: 1;
		transform: translateY(0);
		transition:
			opacity 200ms ease,
			transform 200ms ease;
	}

	.room-header-chrome.is-edit-chrome .room-header-finish > :global(*) {
		width: 100%;
		max-width: 100%;
	}

	@media screen and (max-width: 1123px) {
		.room-header-chrome:not(.is-edit-chrome) .room-header-name {
			margin-right: 15px;
		}
	}

	@media screen and (max-width: 639px) {
		.room-header-chrome:not(.is-edit-chrome) .room-header-name {
			margin-right: 5px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.room-header-back,
		.room-header-name,
		.room-header-actions,
		.room-header-finish {
			transition: none;
		}
	}
</style>
